/**
 * Embudo de onboarding actual (registro + perfil + permisos + deeplinks).
 * Antes vivía como pestaña aparte; ahora se consume desde Aterrizaje.
 * Nota: los eventos se llaman onboarding_v2_* en BD (nombre histórico);
 * en UI no se diferencia “V2” porque ya es el único flujo activo.
 */

import { cn } from '@/lib/utils';
import {
    RefreshCw, UserCheck, Crown, Share2, MapPin, Bell, Droplets, Fish, Users,
} from 'lucide-react';
import { MetricCard } from '@/components/shared/MetricCard';
import {
    useOnboardingAnalytics,
    onboardingStepRate,
    choiceValueLabel,
    type OnboardingChoiceStep,
    type OnboardingClubChoiceRow,
} from '@/hooks/useOnboardingAnalytics';
import { ANALYTICS_DAY_OPTIONS } from '@/components/analytics/periodOptions';

const COLORS = {
    ocean: 'hsl(199, 89%, 48%)',
};

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms} ms`;
    if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
    const min = Math.floor(ms / 60_000);
    const sec = Math.floor((ms % 60_000) / 1000);
    return sec > 0 ? `${min}m ${sec}s` : `${min} min`;
}

interface OnboardingFunnelPanelProps {
    days: number;
    onDaysChange?: (days: number) => void;
    showPeriodSelector?: boolean;
}

export function OnboardingFunnelPanel({
    days,
    onDaysChange,
    showPeriodSelector = true,
}: OnboardingFunnelPanelProps) {
    const { data, isLoading } = useOnboardingAnalytics(days);

    return (
        <div className="space-y-6">
            {showPeriodSelector && onDaysChange && (
                <div className="flex gap-1.5">
                    {ANALYTICS_DAY_OPTIONS.map((d) => (
                        <button
                            key={d}
                            type="button"
                            onClick={() => onDaysChange(d)}
                            className={cn(
                                'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                                days === d
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'bg-muted/50 text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {d} días
                        </button>
                    ))}
                </div>
            )}

            <p className="text-xs text-muted-foreground">
                Flujo de primer uso (registro → nivel → agua → clubes → Comunidad).
                La pantalla CTA de 1ª captura se retiró; el cierre emite path{' '}
                <code className="text-[11px]">no_capture_step</code>. Compara captura/retención
                por path en la sección inferior (before/after).
            </p>

            {isLoading ? (
                <div className="py-16 flex justify-center">
                    <RefreshCw size={24} className="animate-spin text-muted-foreground" />
                </div>
            ) : !data ? (
                <div className="py-16 text-center text-muted-foreground text-sm">
                    No hay datos de onboarding
                </div>
            ) : (
                <>
                    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                        <h3 className="text-sm font-semibold text-foreground">
                            Embudo de onboarding — últimos {days} días
                        </h3>
                        {(() => {
                            const v2 = data.onboarding_v2;
                            const steps = [
                                { label: 'Welcome visto', count: v2.welcome_viewed },
                                { label: 'Nivel elegido', count: v2.level_selected },
                                { label: 'Agua elegida', count: v2.water_selected },
                                { label: 'Clubes vistos', count: v2.clubs_viewed },
                                { label: 'Completado', count: v2.completed },
                            ];
                            // Histórico: si aún hay vistas CTA en el periodo, mostrarlas como fila informativa.
                            if ((v2.first_capture_cta_viewed ?? 0) > 0) {
                                steps.splice(4, 0, {
                                    label: 'CTA 1ª captura vista (legado)',
                                    count: v2.first_capture_cta_viewed ?? 0,
                                });
                            }
                            return steps.map((step, i) => {
                                const prev = i === 0 ? step.count : steps[i - 1].count;
                                const rate = onboardingStepRate(step.count, prev);
                                return (
                                    <FunnelStepRow
                                        key={step.label}
                                        label={step.label}
                                        fromCount={prev}
                                        toCount={step.count}
                                        rate={i === 0 ? 100 : rate}
                                        color={COLORS.ocean}
                                    />
                                );
                            });
                        })()}
                        {data.onboarding_v2.avg_elapsed_ms != null && (
                            <p className="text-xs text-muted-foreground pt-2">
                                Tiempo medio hasta completar:{' '}
                                {formatDuration(data.onboarding_v2.avg_elapsed_ms)}
                            </p>
                        )}
                    </div>

                    <div className="rounded-xl border border-border bg-card p-5 space-y-4">
                        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <Fish size={16} className="text-primary" />
                            Completados con / sin captura
                        </h3>
                        <p className="text-xs text-muted-foreground">
                            Paths históricos: <code className="text-[11px]">start_capture</code> /{' '}
                            <code className="text-[11px]">later</code>. Actual:{' '}
                            <code className="text-[11px]">no_capture_step</code>. «Con captura» =
                            publicaron ≤7 días tras cerrar el Welcome.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                            <MetricCard
                                title="Empezaron captura (legado)"
                                value={data.onboarding_v2.completed_start_capture ?? 0}
                            />
                            <MetricCard
                                title="Más adelante (legado)"
                                value={data.onboarding_v2.completed_later ?? 0}
                            />
                            <MetricCard
                                title="Sin pantalla CTA"
                                value={data.onboarding_v2.completed_no_capture_step ?? 0}
                            />
                            <MetricCard
                                title={`Con captura (${data.onboarding_v2.pct_completed_with_capture ?? 0}%)`}
                                value={data.onboarding_v2.completed_with_capture ?? 0}
                            />
                            <MetricCard
                                title={`Sin captura (${data.onboarding_v2.pct_completed_without_capture ?? 0}%)`}
                                value={data.onboarding_v2.completed_without_capture ?? 0}
                            />
                        </div>
                        {(data.onboarding_v2.path_outcomes?.length ?? 0) > 0 && (
                            <div className="overflow-x-auto rounded-lg border border-border">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-border bg-muted/30">
                                            <th className="text-left px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Path</th>
                                            <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Users</th>
                                            <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Captura ≤7d</th>
                                            <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">% captura</th>
                                            <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">Elig. 7d</th>
                                            <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">% D1</th>
                                            <th className="text-right px-3 py-2 text-xs font-semibold text-muted-foreground uppercase">% D2–D7</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-border">
                                        {data.onboarding_v2.path_outcomes!.map((row) => (
                                            <tr key={row.path} className="hover:bg-muted/20">
                                                <td className="px-3 py-2 font-medium">
                                                    {choiceValueLabel('first_capture_path', row.path)}
                                                </td>
                                                <td className="px-3 py-2 text-right font-mono text-xs">{row.users}</td>
                                                <td className="px-3 py-2 text-right font-mono text-xs">{row.captured_7d}</td>
                                                <td className="px-3 py-2 text-right font-mono text-xs">{row.pct_captured_7d}%</td>
                                                <td className="px-3 py-2 text-right font-mono text-xs">{row.eligible_7d}</td>
                                                <td className="px-3 py-2 text-right font-mono text-xs">{row.pct_ret_d1}%</td>
                                                <td className="px-3 py-2 text-right font-mono text-xs">{row.pct_active_d2_d7}%</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {data.choices && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    <Droplets size={16} className="text-primary" />
                                    Qué eligen en cada pantalla
                                </h3>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Número y % sobre quienes respondieron ese paso en el periodo.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {(data.choices.steps ?? []).map((step) => (
                                    <ChoiceStepCard key={step.step_key} step={step} />
                                ))}
                            </div>

                            {data.choices.modalities && (
                                <ChoiceStepCard
                                    step={data.choices.modalities}
                                    footnote={
                                        (data.choices.modalities.users_with_modes_list ?? 0) === 0
                                            ? 'Lista de modalidades detallada disponible en usuarios nuevos (tracking ampliado).'
                                            : undefined
                                    }
                                />
                            )}

                            <ClubsChoicesCard
                                saw={data.choices.clubs.users_saw_clubs_step}
                                skipped={data.choices.clubs.users_skipped_extras}
                                joinedAny={data.choices.clubs.users_joined_any}
                                byClub={data.choices.clubs.by_club}
                                extras={data.choices.clubs.extras_selected}
                            />
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <MetricCard
                            title="Perfil completado"
                            value={data.profile_onboarding.completed}
                            icon={<UserCheck size={18} />}
                            iconColor="text-teal-400"
                        />
                        <MetricCard
                            title="Trial Pro reclamado"
                            value={data.profile_onboarding.pro_trial_claimed}
                            icon={<Crown size={18} />}
                            iconColor="text-amber-400"
                        />
                        <MetricCard
                            title="Deep-links abiertos"
                            value={data.deeplinks.reduce((s, d) => s + d.opens, 0)}
                            icon={<Share2 size={18} />}
                            iconColor="text-indigo-400"
                        />
                    </div>

                    {data.profile_onboarding.steps?.length > 0 && (
                        <div className="rounded-xl border border-border bg-card overflow-hidden">
                            <h3 className="text-sm font-semibold text-foreground px-5 py-4 border-b border-border">
                                Pasos del perfil (abiertos → completados)
                                {(() => {
                                    const worst = data.profile_onboarding.steps.reduce((best, s) => {
                                        const drop = 100 - onboardingStepRate(s.completed, s.opened);
                                        return !best || drop > best.drop ? { step: s.step, drop } : best;
                                    }, null as { step: string; drop: number } | null);
                                    return worst && worst.drop > 0 ? (
                                        <span className="ml-2 text-xs font-normal text-rose-400">
                                            Mayor abandono: {worst.step} ({worst.drop}%)
                                        </span>
                                    ) : null;
                                })()}
                            </h3>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-muted/30">
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Paso</th>
                                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Abiertos</th>
                                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Completados</th>
                                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Conversión</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {data.profile_onboarding.steps.map((step) => {
                                        const conv = onboardingStepRate(step.completed, step.opened);
                                        const isWorst = data.profile_onboarding.steps.every(
                                            (s) => s === step || conv <= onboardingStepRate(s.completed, s.opened)
                                        ) && conv < 100;
                                        return (
                                            <tr key={step.step} className={cn('hover:bg-muted/20', isWorst && 'bg-rose-500/5')}>
                                                <td className="px-4 py-2 font-medium">{step.step}</td>
                                                <td className="px-4 py-2 text-right font-mono text-xs">{step.opened}</td>
                                                <td className="px-4 py-2 text-right font-mono text-xs">{step.completed}</td>
                                                <td className={cn(
                                                    'px-4 py-2 text-right font-mono text-xs',
                                                    isWorst ? 'text-rose-400 font-semibold' : 'text-muted-foreground'
                                                )}>
                                                    {conv}%
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="rounded-xl border border-border bg-card p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <MapPin size={16} className="text-teal-400" />
                                <h3 className="text-sm font-semibold text-foreground">Permiso ubicación</h3>
                            </div>
                            <p className="text-2xl font-bold text-foreground">
                                {data.permissions.location_intro_shown.toLocaleString()}
                                <span className="text-sm font-normal text-muted-foreground ml-2">intro mostrada</span>
                            </p>
                            {data.permissions.location_result?.length > 0 && (
                                <ul className="mt-3 space-y-1">
                                    {data.permissions.location_result.map((r) => (
                                        <li key={r.result} className="flex justify-between text-xs">
                                            <span className="text-muted-foreground capitalize">{r.result}</span>
                                            <span className="font-mono text-foreground">{r.n}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="rounded-xl border border-border bg-card p-5">
                            <div className="flex items-center gap-2 mb-3">
                                <Bell size={16} className="text-amber-400" />
                                <h3 className="text-sm font-semibold text-foreground">Permiso notificaciones</h3>
                            </div>
                            <p className="text-2xl font-bold text-foreground">
                                {data.permissions.notification_intro_shown.toLocaleString()}
                                <span className="text-sm font-normal text-muted-foreground ml-2">intro mostrada</span>
                            </p>
                            {data.permissions.notification_result?.length > 0 && (
                                <ul className="mt-3 space-y-1">
                                    {data.permissions.notification_result.map((r) => (
                                        <li key={`${r.attempt}-${r.result}`} className="flex justify-between text-xs">
                                            <span className="text-muted-foreground">
                                                intento {r.attempt ?? '—'} · {r.result}
                                            </span>
                                            <span className="font-mono text-foreground">{r.n}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>

                    {data.deeplinks?.length > 0 && (
                        <div className="rounded-xl border border-border bg-card overflow-hidden">
                            <h3 className="text-sm font-semibold text-foreground px-5 py-4 border-b border-border">
                                Deep-links de onboarding
                            </h3>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-border bg-muted/30">
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Origen</th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Destino</th>
                                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Aperturas</th>
                                        <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Usuarios</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {data.deeplinks.map((d) => (
                                        <tr key={`${d.source}-${d.target}`} className="hover:bg-muted/20">
                                            <td className="px-4 py-2 font-medium">{d.source || '—'}</td>
                                            <td className="px-4 py-2 text-muted-foreground">{d.target || '—'}</td>
                                            <td className="px-4 py-2 text-right font-mono text-xs">{d.opens}</td>
                                            <td className="px-4 py-2 text-right font-mono text-xs">{d.unique_users}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function FunnelStepRow({
    label,
    fromCount,
    toCount,
    rate,
    color,
}: {
    label: string;
    fromCount: number;
    toCount: number;
    rate: number;
    color: string;
}) {
    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-foreground">{label}</span>
                <span className="text-muted-foreground">
                    {toCount.toLocaleString()}
                    <span className={cn(
                        'ml-2 font-semibold',
                        rate >= 70 ? 'text-emerald-400' : rate >= 40 ? 'text-amber-400' : 'text-rose-400'
                    )}>
                        {rate}%
                    </span>
                </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                    className="h-full rounded-full transition-all"
                    style={{
                        width: `${fromCount > 0 ? Math.max(4, (toCount / fromCount) * 100) : 0}%`,
                        backgroundColor: color,
                    }}
                />
            </div>
        </div>
    );
}

function ChoiceStepCard({
    step,
    footnote,
}: {
    step: OnboardingChoiceStep;
    footnote?: string;
}) {
    const total = step.users_with_choice || 0;
    return (
        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
                <div>
                    <h4 className="text-sm font-semibold text-foreground">{step.step_label}</h4>
                    <p className="text-[11px] text-muted-foreground">{total.toLocaleString()} con elección</p>
                </div>
                <Fish size={14} className="text-muted-foreground shrink-0 mt-0.5" />
            </div>
            {step.options.length === 0 ? (
                <p className="text-xs text-muted-foreground">Sin datos en este periodo</p>
            ) : (
                <ul className="space-y-2">
                    {step.options.map((opt) => (
                        <li key={opt.value} className="space-y-1">
                            <div className="flex items-center justify-between text-xs gap-2">
                                <span className="text-foreground font-medium truncate">
                                    {choiceValueLabel(step.step_key, opt.value)}
                                </span>
                                <span className="text-muted-foreground shrink-0 font-mono">
                                    {opt.users} · {opt.pct}%
                                </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                                <div
                                    className="h-full rounded-full bg-primary"
                                    style={{ width: `${Math.min(100, Math.max(2, opt.pct))}%` }}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
            {footnote && <p className="text-[11px] text-muted-foreground">{footnote}</p>}
        </div>
    );
}

function ClubsChoicesCard({
    saw,
    skipped,
    joinedAny,
    byClub,
    extras,
}: {
    saw: number;
    skipped: number;
    joinedAny: number;
    byClub: OnboardingClubChoiceRow[];
    extras: OnboardingClubChoiceRow[];
}) {
    return (
        <div className="rounded-xl border border-border bg-card p-4 space-y-4">
            <div className="flex items-start gap-2">
                <Users size={16} className="text-primary mt-0.5 shrink-0" />
                <div>
                    <h4 className="text-sm font-semibold text-foreground">Clubes en onboarding</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">
                        {saw.toLocaleString()} vieron la pantalla · {joinedAny.toLocaleString()} se unieron a ≥1 club
                        · {skipped.toLocaleString()} saltaron extras
                    </p>
                </div>
            </div>

            <ClubTable
                title="Clubes a los que se unieron"
                subtitle="% sobre quienes vieron el paso de clubes. Auto = inscripción oficial automática."
                rows={byClub}
                showOfficial
            />

            {extras.length > 0 && (
                <ClubTable
                    title="Extras elegidos (eventos nuevos)"
                    subtitle="Solo usuarios que ya envían club_ids en analytics."
                    rows={extras}
                />
            )}
        </div>
    );
}

function ClubTable({
    title,
    subtitle,
    rows,
    showOfficial,
}: {
    title: string;
    subtitle: string;
    rows: OnboardingClubChoiceRow[];
    showOfficial?: boolean;
}) {
    if (rows.length === 0) {
        return <p className="text-xs text-muted-foreground">{title}: sin datos en el periodo</p>;
    }
    return (
        <div className="space-y-2">
            <div>
                <p className="text-xs font-semibold text-foreground">{title}</p>
                <p className="text-[11px] text-muted-foreground">{subtitle}</p>
            </div>
            <div className="overflow-x-auto rounded-lg border border-border/60">
                <table className="w-full text-xs min-w-[480px]">
                    <thead>
                        <tr className="text-muted-foreground border-b border-border bg-muted/30">
                            <th className="text-left px-3 py-2 font-medium">Club</th>
                            {showOfficial && <th className="text-left px-3 py-2 font-medium">Tipo</th>}
                            <th className="text-right px-3 py-2 font-medium">Usuarios</th>
                            {showOfficial && <th className="text-right px-3 py-2 font-medium">Auto</th>}
                            {showOfficial && <th className="text-right px-3 py-2 font-medium">Manual</th>}
                            <th className="text-right px-3 py-2 font-medium">%</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.club_id} className="border-b border-border/40">
                                <td className="px-3 py-2 text-foreground font-medium">{r.club_name}</td>
                                {showOfficial && (
                                    <td className="px-3 py-2 text-muted-foreground">
                                        {r.is_official ? 'Oficial' : 'Comunidad'}
                                    </td>
                                )}
                                <td className="px-3 py-2 text-right font-mono">{r.users}</td>
                                {showOfficial && (
                                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                                        {r.users_auto ?? 0}
                                    </td>
                                )}
                                {showOfficial && (
                                    <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                                        {r.users_manual ?? 0}
                                    </td>
                                )}
                                <td className="px-3 py-2 text-right font-mono text-primary">
                                    {r.pct_of_cohort ?? r.pct_of_joined_events ?? 0}%
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
